import React, { useEffect, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import { calculateAverage, calculatePercentile } from '../utils/Stats';
import { useTranslation } from 'react-i18next';
import MonitorTitle from './MonitorTitle';

function dateTickFormatter(value, timespan) {
  if (timespan === '1day') {
    return moment(value * 1000).format('LT');
  } else {
    return moment(value * 1000).format('L');
  }
}

function dateFormatter(value, timespan) {
  if (timespan === '1day') {
    return moment(value * 1000).format('LLL');
  } else {
    return moment(value * 1000).format('L');
  }
}

function uptimeTickFormatter(value) {
  return (value * 100) + ' %';
}

function latencyTickFormatter(value) {
  if (value > 1000) {
    return (value / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' s';
  }
  return value + ' ms';
}

function tooltipFormatter(value, name, { dataKey }) {
  switch (dataKey) {
    case 'uptime':
      return [ (value * 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' %', name ];
    case 'latency':
      return [ value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ms', name ];
    default:
    return [ value, name ];
  }
}

function dayStart(x) {
  return x - x % 86400;
}

function startDateFromTimespan(timespan) {
  switch (timespan) {
  case '1day':
    return moment().subtract(1, 'day').unix();
  case '1week':
    return moment().subtract(1, 'weeks').unix();
  case '1month':
    return moment().subtract(1, 'months').unix();
  case '6months':
    return moment().subtract(6, 'months').unix();
  case '1year':
    return moment().subtract(1, 'year').unix();
  default:
    return 0;
  }
}

function endDateFromTimespan(timespan) {
  const now = moment().unix();
  switch (timespan) {
  case '1day':
    return now;
  default:
    return dayStart(now);
  }
}

function intervalFromTimespan(timespan) {
  switch (timespan) {
  case '1day':
    return 15 * 60;
  default:
    return 60 * 60 * 24;
  }
}

const CHART_STROKE = '#0D9488';
const CHART_FILL_START = '#DAF5F0';
const CHART_DOT = '#0D9488';

function Chart({ data, timespan, dataKey, title, tickFormatter, syncId }) {
  const tickStyle = { fill: '#777777', fontSize: 11 };

  return (
    <ResponsiveContainer width='100%' height={130}>
      <AreaChart data={data} syncId={syncId}>
        <defs>
          <linearGradient id={`gradient-${dataKey}`} x1={0} y1={0} x2={0} y2={1}>
            <stop offset='5%' stopColor={CHART_FILL_START} stopOpacity={0.9}/>
            <stop offset='95%' stopColor={CHART_FILL_START} stopOpacity={0.1}/>
          </linearGradient>
        </defs>

        <XAxis
          dataKey='date'
          type='category'
          tick={tickStyle}
          tickFormatter={x => dateTickFormatter(x, timespan)}
          minTickGap={100}
          axisLine={{ stroke: '#e5e7eb' }}
          tickLine={false}
        />
        <YAxis
          type='number'
          tick={tickStyle}
          tickFormatter={tickFormatter}
          axisLine={false}
          tickLine={false}
          width={60}
        />

        <Tooltip
          contentStyle={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,.07)',
            fontSize: 12,
            padding: '8px 12px',
          }}
          labelStyle={{ color: '#777777', marginBottom: 4 }}
          labelFormatter={x => dateFormatter(x, timespan)}
          formatter={tooltipFormatter}
        />

        <Area
          type='monotone'
          dot={false}
          activeDot={{ r: 3, strokeWidth: 0, fill: CHART_DOT }}
          dataKey={dataKey}
          isAnimationActive={false}
          name={title}
          fill={`url(#gradient-${dataKey})`}
          stroke={CHART_STROKE}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function LoadingBar() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 animate-pulse">
      <div className="h-5 bg-gray-100 rounded w-1/4 mb-6" />
      <div className="h-32 bg-gray-50 rounded" />
    </div>
  );
}

export default function Monitor({ monitor, timespan }) {
  const { t } = useTranslation();

  const [data, setData] = useState();
  const [overallLatency, setOverallLatency] = useState(-1);
  const [overallUptime, setOverallUptime] = useState(-1);

  useEffect(() => {
    const startDate = startDateFromTimespan(timespan);
    const endDate = endDateFromTimespan(timespan);
    const interval = intervalFromTimespan(timespan);

    const dates = [];
    for (let d = startDate - startDate % interval; d <= endDate; d += interval) {
      if (d >= startDate) {
        dates.push(d);
      }
    }

    const tsData = (timespan === '1day' ? monitor.timeSeriesData.last24Hours : monitor.timeSeriesData.last12Months)
      .reduce((prev, x) => ({
        ...prev,
        [x[0]]: {
          date: x[0],
          latency: x[1] !== -1 ? x[1] : undefined,
          uptime: x[3] > 0 ? x[2] / x[3] : undefined
        }
      }), {});

    const newData = dates.map(x => (tsData[x] || { date: x }));
    setData(newData);
    setOverallUptime(calculateAverage(newData.map(x => x.uptime).filter(x => x !== undefined)));
    setOverallLatency(calculatePercentile(newData.map(x => x.latency).filter(x => x !== undefined), monitor.percentile));
  }, [monitor, timespan]);

  if (!data) return <LoadingBar />;

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Title with yellow left accent */}
      <div className="flex items-stretch border-b border-gray-100">
        <div className="w-1 bg-primary flex-shrink-0" />
        <div className="px-5 py-3.5">
          <h3 className="font-semibold">
            <MonitorTitle title={monitor.title} />
          </h3>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {/* Availability */}
        <div>
          <div className="flex justify-between items-baseline mb-3">
            <span className="text-sm font-medium text-neutral">
              {t('status.availability')}
            </span>
            <span className="text-sm font-semibold tabular-nums">
              {overallUptime >= 0 && tooltipFormatter(overallUptime, 'uptime', { dataKey: 'uptime' })[0]}
            </span>
          </div>
          <Chart
            syncId={monitor.index}
            data={data}
            timespan={timespan}
            dataKey='uptime'
            title={t('status.availability')}
            tickFormatter={uptimeTickFormatter}
          />
        </div>

        {/* Latency */}
        <div>
          <div className="flex justify-between items-baseline mb-3">
            <span className="text-sm font-medium text-neutral">
              {t('status.latency')}
            </span>
            <span className="text-sm font-semibold tabular-nums">
              {overallLatency >= 0 && tooltipFormatter(overallLatency, 'latency', { dataKey: 'latency' })[0]}
            </span>
          </div>
          <Chart
            syncId={monitor.index}
            data={data}
            timespan={timespan}
            dataKey='latency'
            title={t('status.latency')}
            tickFormatter={latencyTickFormatter}
          />
        </div>
      </div>
    </div>
  );
}
